import React, { useEffect, useMemo, useState } from 'react';
import config from '../config';

/**
 * Coating color code: shared picker + swatch.
 *
 * Backs the requested-finish-color feature. The value is a free-text CODE
 * ("RAL 9005", "ANSI 61", "gloss black", "match the sample we sent") plus an
 * optional friendly name and an approximate hex used only to draw a chip.
 *
 * TWO RULES, both mirrored from jobq-api/coating_colors.py:
 *
 *  1. The typed text is never rewritten. RAL encodes no gloss level, so real
 *     specs read "RAL 9005 matte black" — collapsing that to "RAL 9005" would
 *     drop the gloss and the coater would spray the wrong finish. We only ever
 *     *add* a name/chip next to what the operator typed.
 *  2. A chip is never fabricated. Unknown codes and two-tone specs show the code
 *     with no chip rather than a confidently wrong color. The chip is a
 *     typo-catcher ("that should be black, not yellow"), never a color match —
 *     these standards are physical chips, sRGB can't represent them, and gloss
 *     and texture shift the appearance further.
 *
 * The catalogue comes from `GET /coating_colors` so there is ONE source of truth
 * rather than a copy of the hex table in JS. `matchCatalog` below is a
 * deliberately simplified local matcher used ONLY for the live preview while
 * typing; the API re-resolves authoritatively on save, so any divergence
 * self-corrects on the next load.
 */

const API_BASE = config.API_BASE_URL;

export const COATING_COLOR_DATALIST_ID = 'coating-color-options';

// Module-level cache: the catalogue is static reference data and several pages
// mount this at once.
let _catalogCache = null;
let _catalogPromise = null;

export function useCoatingColors() {
    const [catalog, setCatalog] = useState(_catalogCache || []);
    useEffect(() => {
        let alive = true;
        // Do NOT return early on a populated cache: the cache can fill between
        // this component's render and this effect flushing, and useState only
        // reads its initializer once — the field would sit on a stale [].
        if (_catalogCache) {
            setCatalog(_catalogCache);
            return undefined;
        }
        if (!_catalogPromise) {
            _catalogPromise = fetch(`${API_BASE}/coating_colors`)
                .then((r) => (r.ok ? r.json() : null))
                .then((data) => {
                    // Only cache a REAL catalog. A 4xx/5xx or an empty body used
                    // to be cached as [] forever, so one bad response during a
                    // deploy left every color field with no suggestions and no
                    // chips for the rest of the session.
                    if (Array.isArray(data) && data.length) {
                        _catalogCache = data;
                        return data;
                    }
                    _catalogPromise = null;   // let the next mount retry
                    return [];
                })
                .catch((err) => {
                    console.error('Failed to load coating colors', err);
                    _catalogPromise = null;   // let the next mount retry
                    return [];
                });
        }
        _catalogPromise.then((data) => { if (alive) setCatalog(data || []); });
        return () => { alive = false; };
    }, []);
    return catalog;
}

/** Uppercase, strip non-alphanumerics, fold GREY→GRAY. Mirrors _compact(). */
function compact(raw) {
    return String(raw == null ? '' : raw)
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '')
        .replace(/GREY/g, 'GRAY');
}

/**
 * Best-effort catalogue match for the live preview. Returns an entry or null.
 * Simplified on purpose (see the file header) — the API is the authority.
 */
export function matchCatalog(code, catalog) {
    const c = compact(code);
    if (!c || !catalog || !catalog.length) return null;

    // Two or more standard tokens = a two-tone spec. No chip, same as the API.
    const tokens = c.match(/RAL\d{4}|(?:ANSI|ASA)\d{2}/g);
    if (tokens && tokens.length > 1) return null;

    let key = null;
    let m = c.match(/^RAL(\d{4})/);
    if (m) key = `RAL${m[1]}`;
    if (!key) { m = c.match(/^(?:ANSI|ASA)(\d{2})/); if (m) key = `ANSI${m[1]}`; }
    if (!key && /^\d{4}$/.test(c)) key = `RAL${c}`;

    if (key) return catalog.find((e) => e.key === key) || null;
    return catalog.find((e) => compact(e.code) === c || compact(e.name) === c) || null;
}

/**
 * Read-only chip + code. Use anywhere a color needs to be shown (queues,
 * tables, modals). Renders nothing when there is no code.
 *
 * The chip always carries a border so a white (RAL 9010) or black (RAL 9005)
 * color stays visible in both light and dark themes — `--jq-border-medium` is
 * defined for both in dark-mode.css. Never wrap this in `.dark-invertible`,
 * which would invert the actual color.
 */
export function CoatingSwatch({ code, name, hex, size = 14, showCode = true, className = '' }) {
    if (!code) return null;
    return (
        <span className={`d-inline-flex align-items-center gap-1 ${className}`}
              title={`${code}${name ? ` — ${name}` : ''}\nChip is approximate — not a color match.`}>
            {hex ? (
                <span
                    aria-hidden="true"
                    style={{
                        display: 'inline-block',
                        width: size,
                        height: size,
                        backgroundColor: hex,
                        border: '1px solid var(--jq-border-medium, #999)',
                        borderRadius: 3,
                        flexShrink: 0,
                    }}
                />
            ) : null}
            {showCode ? <span>{code}</span> : null}
        </span>
    );
}

/**
 * Editable color field: free-text input backed by a <datalist> of the
 * standards catalogue, with a live chip and — for codes the catalogue doesn't
 * know — an optional custom chip picker so a house color can still show one.
 *
 * onChange receives the full triple `{ coating_color_code, coating_color_name,
 * coating_color_hex }` so callers can spread it straight into their state.
 * Field names are configurable because OPO lines use `color_*` while parts and
 * assembly rows use `coating_color_*`.
 */
export function CoatingColorField({
    code,
    name,
    hex,
    onChange,
    disabled = false,
    label = 'Coating Color / Code',
    placeholder = 'e.g. RAL 9005, ANSI 61, Gloss Black',
    fieldPrefix = 'coating_color_',
    showLabel = true,
    small = true,
    help = null,
}) {
    const catalog = useCoatingColors();
    const [showCustom, setShowCustom] = useState(false);
    // A chip the OPERATOR set with the picker, for this editing session only.
    // Tracked separately from the `hex` prop because the prop cannot tell us
    // whether a stored hex was operator-chosen or catalog-derived, and carrying
    // a catalog-derived hex onto a different code would attach one color's chip
    // to another color's code — the exact thing this feature promises not to do.
    const [customHex, setCustomHex] = useState(null);

    const match = useMemo(() => matchCatalog(code, catalog), [code, catalog]);
    // Explicit hex wins (an operator override); otherwise borrow the catalog's.
    const effectiveHex = hex || (match ? match.hex : null);
    const effectiveName = name || (match ? match.name : null);
    const hasCode = !!(code && String(code).trim());
    const unknown = hasCode && !match;

    const emit = (next) => {
        onChange({
            [`${fieldPrefix}code`]: next.code,
            [`${fieldPrefix}name`]: next.name,
            [`${fieldPrefix}hex`]: next.hex,
        });
    };

    const handleCodeChange = (raw) => {
        const value = raw;
        const hit = matchCatalog(value, catalog);
        if (!value || !value.trim()) {
            // Clearing the code clears the whole triple — a name or a chip with
            // no code is not something a coater can act on.
            emit({ code: null, name: null, hex: null });
            setShowCustom(false);
            setCustomHex(null);
            return;
        }
        if (hit) {
            // A recognized code takes the catalog's name and chip; any custom
            // chip from a previous code is discarded (the catalog owns it now).
            setCustomHex(null);
            emit({ code: value, name: hit.name, hex: null });
            return;
        }
        // An unrecognized code gets NO chip unless the operator sets one for
        // THIS code with the picker. Inheriting the previous code's hex here is
        // how a "RAL 9005 -> HOUSE-RED" edit used to end up storing Jet Black's
        // chip against a house red, which the API then persisted as a
        // deliberate operator override.
        emit({ code: value, name: null, hex: customHex || null });
    };

    const inputCls = small ? 'form-control form-control-sm' : 'form-control';

    return (
        <>
            {showLabel && (
                <label className="form-label text-muted small fw-bold">{label}</label>
            )}
            <div className={`input-group ${small ? 'input-group-sm' : ''}`}>
                <span
                    className="input-group-text"
                    style={{ paddingLeft: 8, paddingRight: 8 }}
                    title={effectiveHex
                        ? 'Approximate color chip — NOT a color match. Coat to the specified standard.'
                        : 'No chip available for this code — the code itself is the spec.'}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            display: 'inline-block',
                            width: 16,
                            height: 16,
                            backgroundColor: effectiveHex || 'transparent',
                            backgroundImage: effectiveHex
                                ? 'none'
                                : 'repeating-linear-gradient(45deg, var(--jq-border,#ccc) 0 3px, transparent 3px 6px)',
                            border: '1px solid var(--jq-border-medium, #999)',
                            borderRadius: 3,
                        }}
                    />
                </span>
                <input
                    type="text"
                    className={inputCls}
                    list={COATING_COLOR_DATALIST_ID}
                    value={code || ''}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(e) => handleCodeChange(e.target.value)}
                />
                {unknown && !disabled && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="This code isn't in the standards list. Set an approximate chip for it."
                        onClick={() => setShowCustom((v) => !v)}
                    >
                        🎨
                    </button>
                )}
                {showCustom && unknown && !disabled && (
                    <input
                        type="color"
                        className="form-control form-control-color"
                        style={{ maxWidth: 46 }}
                        value={hex || '#808080'}
                        title="Approximate chip for this custom color"
                        onChange={(e) => {
                            const v = e.target.value.toUpperCase();
                            setCustomHex(v);
                            emit({ code, name: name || null, hex: v });
                        }}
                    />
                )}
            </div>

            <CoatingColorDatalist catalog={catalog} />

            <div className="small text-muted mt-1" style={{ lineHeight: 1.25 }}>
                {hasCode && effectiveName && <span>{effectiveName}. </span>}
                {unknown && <span className="text-warning-emphasis">Not a standard code — stored as typed. </span>}
                {hasCode && effectiveHex && <span>Chip is approximate — not a color match. </span>}
                {help}
            </div>
        </>
    );
}

/**
 * The shared <datalist>. Safe to render more than once per page (duplicate ids
 * resolve to the first, and every copy holds the same options), so each field
 * can render its own without callers having to hoist it.
 */
export function CoatingColorDatalist({ catalog: catalogProp }) {
    const fetched = useCoatingColors();
    const catalog = catalogProp || fetched;
    return (
        <datalist id={COATING_COLOR_DATALIST_ID}>
            {(catalog || []).map((e) => (
                <option key={e.key} value={e.code}>
                    {e.name}{e.group ? ` (${e.group})` : ''}
                </option>
            ))}
        </datalist>
    );
}

export default CoatingColorField;
